import AdminUser from '../../models/admin/AdminUser';
import BackendService from '../../services/admin/BackendService';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import {
  ContentReviewInterface,
  createEmptyReviewInterface,
} from '../../models/ContentReviewInterface';
import { ReviewComment, ReviewRequest, ReviewStatus } from '../../models/ReviewRequest';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { applyAcceptedChangesToDraft, getContentTypeFromReviewType } from './reviewViewerUtils';

export type ReviewViewMode = 'read' | 'edit' | 'diff' | 'comparison';

export function useReviewViewer() {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();

  const [reviewRequest, setReviewRequest] = useState<ReviewRequest | null>(null);
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [publishedContent, setPublishedContent] = useState<ContentManagementInterface | null>(null);
  const [draftContent, setDraftContent] = useState<ContentManagementInterface | null>(null);
  const [reviewData, setReviewData] = useState<ContentReviewInterface | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ReviewViewMode>('read');
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const loadReviewData = useCallback(async () => {
    if (!reviewId) return;

    setLoading(true);
    setError(null);

    try {
      const backendService = new BackendService();

      const [reviewRequestData, commentsData, currentUserData] = await Promise.all([
        backendService.getReviewRequest(reviewId),
        backendService.getReviewComments(reviewId),
        backendService.getCurrentUser(),
      ]);

      setReviewRequest(reviewRequestData);
      setComments(commentsData.data);
      setCurrentUser(currentUserData);

      const fundOpportunityId = reviewRequestData.fundOpportunity.id;
      const contentType = getContentTypeFromReviewType(reviewRequestData.type);

      const [publishedContentStr, draftContentStr] = await Promise.all([
        backendService.getContentManagementWithToken(contentType, fundOpportunityId, false),
        backendService.getContentManagementWithToken(contentType, fundOpportunityId, true),
      ]);

      if (!publishedContentStr || !draftContentStr) {
        setError('Published or draft content not found for this fund opportunity');
        return;
      }

      const parsedPublished = JSON.parse(publishedContentStr);
      const parsedDraft = JSON.parse(draftContentStr);
      setPublishedContent(parsedPublished);
      setDraftContent(parsedDraft);

      try {
        const existingReviewData = await backendService.getReviewData(reviewId);
        setReviewData(
          existingReviewData ||
            createEmptyReviewInterface(reviewId, fundOpportunityId, parsedPublished, parsedDraft),
        );
      } catch {
        setReviewData(
          createEmptyReviewInterface(reviewId, fundOpportunityId, parsedPublished, parsedDraft),
        );
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load review data');
      console.error('Error loading review data:', err);
    } finally {
      setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => {
    if (reviewId) {
      void loadReviewData();
    }
  }, [reviewId, loadReviewData]);

  const handleStatusUpdate = async (newStatus: ReviewStatus) => {
    if (!reviewRequest) return;

    try {
      await new BackendService().updateReviewStatus(reviewRequest.id, { status: newStatus });
      await loadReviewData();
    } catch (err: any) {
      setError(err.message || 'Failed to update review status');
      console.error('Error updating review status:', err);
    }
  };

  const handleCommentSubmit = async (
    comment: string,
    referencedItem?: string,
    contentText?: string,
    parentCommentId?: string,
  ) => {
    if (!reviewRequest) return;

    try {
      const backendService = new BackendService();
      await backendService.submitComment(reviewRequest.id, {
        comments: comment,
        referencedItem,
        contentText,
        parentCommentExternalId: parentCommentId,
      });

      const commentsData = await backendService.getReviewComments(reviewRequest.id);
      setComments(commentsData.data);
    } catch (err: any) {
      setError(err.message || 'Failed to submit comment');
      console.error('Error submitting comment:', err);
    }
  };

  const handleReviewDataUpdate = async (updatedReviewData: ContentReviewInterface) => {
    if (!reviewId) return;

    setSaving(true);
    try {
      await new BackendService().uploadReviewData(reviewId, updatedReviewData);
      setReviewData(updatedReviewData);
    } catch (err: any) {
      setError(err.message || 'Failed to save review data');
      console.error('Error saving review data:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAcceptedChangesToDraft = async () => {
    if (!reviewData || !draftContent || !reviewRequest) return;

    setSavingDraft(true);
    try {
      const backendService = new BackendService();
      const updatedDraftContent = applyAcceptedChangesToDraft(reviewData, draftContent);
      const contentType = getContentTypeFromReviewType(reviewRequest.type);

      await backendService.uploadContentManagementUpdate(
        contentType,
        reviewRequest.fundOpportunity.id,
        updatedDraftContent,
      );

      setError(null);
      await loadReviewData();
    } catch (err: any) {
      setError(err.message || 'Failed to save accepted changes to draft');
      console.error('Error saving accepted changes to draft:', err);
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (!reviewRequest) return;

    try {
      const contentType = getContentTypeFromReviewType(reviewRequest.type);
      await new BackendService().publishContentManagement(
        contentType,
        reviewRequest.fundOpportunity.id,
      );
      setError(null);
      await loadReviewData();
    } catch (err: any) {
      setError(err.message || 'Failed to publish content');
      console.error('Error publishing content:', err);
    }
  };

  return {
    reviewId,
    navigate,
    reviewRequest,
    comments,
    currentUser,
    publishedContent,
    draftContent,
    reviewData,
    loading,
    error,
    viewMode,
    setViewMode,
    saving,
    savingDraft,
    loadReviewData,
    handleStatusUpdate,
    handleCommentSubmit,
    handleReviewDataUpdate,
    handleSaveAcceptedChangesToDraft,
    handlePublish,
  };
}
